import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap, switchMapTo } from "rxjs/operators";

import { AppError } from "../../../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../../../auth/auth.action";
import { CustomerManagementAction } from "../../../customer-management/customer-management.action";
import { MessageScopeType, MessageType } from "../../../message/message.model";
import { MessageService } from "../../../message/message.service";
import { DepositLimitConfigService } from "../deposit-limit.config";

import { MULTI_DEPOSIT_LIMIT_ACTION_TYPE, MultiDepositLimitAction } from "./multi-deposit-limit.action";
import { MultiDepositLimitConfigService } from "./multi-deposit-limit.config";
import { MULTI_DEPOSIT_LIMIT_MESSAGE_CODES, MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT } from "./multi-deposit-limit.const";
import { MultiDepositLimit } from "./multi-deposit-limit.model";
import { MultiDepositLimitService } from "./multi-deposit-limit.service";

/**
 * @deprecated [fabric-deprecated] - Use MultiDepositLimitService from app-responsible-gaming instead
 */
@Injectable()
export class MultiDepositLimitEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetMultiDepositLimits instead
	 */
	multiDepositLimits$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_DEPOSIT_LIMIT_ACTION_TYPE.get, AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.config.getMultiDepositLimitEnabled()),
			switchMap(() =>
				this.service.get().pipe(
					map((response) => this.action.getSuccess(response)),
					catchError((error: AppError) => of(this.action.getFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateMultiDepositLimit instead
	 */
	multiDepositLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_DEPOSIT_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<MultiDepositLimit>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map((response) => this.action.updateSuccess(response)),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateMultiDepositLimit instead
	 */
	multiDepositLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_DEPOSIT_LIMIT_ACTION_TYPE.updateSuccess),
			map(() => this.customerAction.getCustomer())
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateMultiDepositLimit instead
	 */
	updateSuccessGetMultiDepositLimitsByJurisdiction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_DEPOSIT_LIMIT_ACTION_TYPE.updateSuccess),
			switchMapTo(this.depositLimitConfigService.getIsMultiDepositLimitReconfirmationEnabled$()),
			filter((isMultiDepositLimitReconfirmationEnabled) => !!isMultiDepositLimitReconfirmationEnabled),
			map(() => this.action.get())
		)
	);

	clearOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.clear())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[MULTI_DEPOSIT_LIMIT_ACTION_TYPE.getFail]: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.get,
				[MULTI_DEPOSIT_LIMIT_ACTION_TYPE.updateFail]: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
			}),
			this.messageService.mapClearByContext$({
				[MULTI_DEPOSIT_LIMIT_ACTION_TYPE.get]: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.get,
				[MULTI_DEPOSIT_LIMIT_ACTION_TYPE.update]: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
			}),
			this.messageService.map$({
				[MULTI_DEPOSIT_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MULTI_DEPOSIT_LIMIT_MESSAGE_CODES.updateSuccess,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: MultiDepositLimitAction,
		private customerAction: CustomerManagementAction,
		private service: MultiDepositLimitService,
		private config: MultiDepositLimitConfigService,
		private depositLimitConfigService: DepositLimitConfigService,
		private messageService: MessageService
	) {}
}
